import { DropdownItemDataType } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import { AdCampaignPureType } from '@shared/api/graphql/generated';
import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { CompanyTagType } from '@entities/company/api/types';
import { IAdCampaignsMetadata } from '../../providers/types';
import { parseNullableDate } from '../../providers/utilities/parseDate';
import { TEXTS } from '../texts';

export type UseAdCampaignsModalControllerParams = {
  adCampaigns: IAdCampaignsMetadata;
};

export enum AdCampaignsGroupName {
  Active = 'active',
  Planned = 'planned',
  Expired = 'expired',
  Stopped = 'stopped',
}

const useAdCampaignsModalController = ({ adCampaigns }: UseAdCampaignsModalControllerParams) => {
  const [searchValue, setSearchValue] = useState<string>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const filteredAdCampaigns = useMemo(
    () =>
      Object.values(adCampaigns).filter(
        (ad) =>
          (!searchValue || ad.title.toLocaleLowerCase().includes(searchValue)) &&
          (selectedTags.length === 0 || ad.tags?.some((tag) => selectedTags.includes(tag.title)))
      ),
    [searchValue, selectedTags]
  );

  const adCampaignGroups = useMemo(
    () =>
      Object.values(filteredAdCampaigns).reduce<Record<AdCampaignsGroupName, AdCampaignPureType[]>>(
        (groups, adCampaign) => {
          const startDate = parseNullableDate(adCampaign.timingSettings.startDate);
          const endDate = parseNullableDate(adCampaign.timingSettings.endDate);
          const isEndDateValid = DateUtils.isValid(endDate);
          const now = new Date();

          if (!adCampaign.enable) {
            groups[AdCampaignsGroupName.Stopped].push(adCampaign);
          } else if (
            (isEndDateValid && DateUtils.isDateWithinInterval(now, { start: startDate, end: endDate })) ||
            (DateUtils.isDateAfter(now, startDate) && !isEndDateValid)
          ) {
            groups[AdCampaignsGroupName.Active].push(adCampaign);
          } else if (DateUtils.isDateBefore(now, startDate)) {
            groups[AdCampaignsGroupName.Planned].push(adCampaign);
          } else if (DateUtils.isDateAfter(now, endDate)) {
            groups[AdCampaignsGroupName.Expired].push(adCampaign);
          }
          return groups;
        },
        {
          [AdCampaignsGroupName.Active]: [],
          [AdCampaignsGroupName.Planned]: [],
          [AdCampaignsGroupName.Expired]: [],
          [AdCampaignsGroupName.Stopped]: [],
        }
      ),
    [filteredAdCampaigns]
  );

  const adCampaignGroupTitles = {
    [AdCampaignsGroupName.Active]: TEXTS.AD_CAMPAIGN_TYPE_ACTIVE,
    [AdCampaignsGroupName.Planned]: TEXTS.AD_CAMPAIGN_TYPE_PLANNED,
    [AdCampaignsGroupName.Expired]: TEXTS.AD_CAMPAIGN_TYPE_EXPIRED,
    [AdCampaignsGroupName.Stopped]: TEXTS.AD_CAMPAIGN_TYPE_STOPPED,
  };

  const filterAdCampaignTags = (_tag: CompanyTagType) => true;

  const onSearchChange: (value: string) => void = debounce((value: string) => {
    const searchValue = value.trim().toLocaleLowerCase();
    setSearchValue(searchValue);
  }, 100);

  const onTagFilterChange = (tags: DropdownItemDataType[]) => {
    setSelectedTags(tags.map((tag) => tag.value) as string[]);
  };

  return {
    filteredAdCampaigns,
    adCampaignGroups,
    adCampaignGroupTitles,
    filterAdCampaignTags,
    onSearchChange,
    onTagFilterChange,
  };
};

export default useAdCampaignsModalController;
